import {
  Box,
  Button,
  Dialog,
  Divider,
  Stack,
  SvgIcon,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  LockOpenOutlined,
  LockOutlined,
  PhonelinkOutlined,
  VerifiedUserOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { useState } from 'react';
import useAuth from '@hooks/useAuth';
import DialogManageSessions from './dialog-manage-sessions';
import { useAppSelector, useAppDispatch } from '@store/index';
import { setMFAStatus } from '@store/slices/user.slice';
import useToast from '@hooks/useToast';

interface DialogSetupMFAProps extends DialogManageSessions {
  onMFASetupComplete?: () => void;
}

type IconVariant = 'enable' | 'disable' | 'verify';

const IconCircle = ({
  icon,
  variant,
}: {
  icon: React.ElementType;
  variant: IconVariant;
}) => {
  const isError = variant === 'disable';
  return (
    <Box
      sx={{
        bgcolor: isError ? '#FCEBEB' : '#E6F1FB',
        border: `1.5px solid ${isError ? '#F7C1C1' : '#B5D4F4'}`,
        borderRadius: '50%',
        width: 56,
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <SvgIcon
        component={icon}
        color={isError ? 'error' : 'primary'}
        sx={{ fontSize: 24 }}
      />
    </Box>
  );
};

const SetupMFA = ({
  open,
  onClose,
  onMFASetupComplete,
}: DialogSetupMFAProps) => {
  const { setupMFAAuth, verifyMFAAuth, disableMFAAuth } = useAuth();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState<'initial' | 'verify'>('initial');
  const [mfaSecret, setMfaSecret] = useState<any>(null);
  const isMFAEnabled = useAppSelector((state) => state.user.isMFAEnabled);
  const mfaStatus = !isMFAEnabled;

  const { toast } = useToast();

  const handleClose = () => {
    setStep('initial');
    setVerificationCode('');
    setMfaSecret(null);
    onClose();
  };

  const handleSetupMFA = async () => {
    try {
      setLoading(true);
      if (mfaStatus) {
        const res = await setupMFAAuth();
        if (res?.data) {
          setMfaSecret(res.data);
          setStep('verify');
        }
      } else {
        const res = await disableMFAAuth();
        if (res?.status === 200) {
          dispatch(setMFAStatus(false));
          handleClose();
          toast({
            message: 'MFA disabled successfully',
            severity: 'success',
          });
        }
      }
    } catch (error) {
      console.error('MFA Setup Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMFA = async () => {
    try {
      setLoading(true);
      const res = await verifyMFAAuth(verificationCode);
      if (res?.status === 200) {
        dispatch(setMFAStatus(true));
        toast({
          message: 'MFA enabled successfully',
          severity: 'success',
        });
        onMFASetupComplete?.();
        handleClose();
      }
    } catch (error) {
      console.error('MFA Verification Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth='xs'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
        },
      }}
    >
      <Stack sx={{ px: '24px', pt: '24px', pb: '20px' }} gap={2.25}>
        {step === 'initial' ? (
          <>
            <Stack alignItems='center' gap={1.25}>
              <IconCircle
                icon={mfaStatus ? VerifiedUserOutlined : LockOpenOutlined}
                variant={mfaStatus ? 'enable' : 'disable'}
              />
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '15px',
                  textAlign: 'center',
                  lineHeight: 1.4,
                }}
              >
                {mfaStatus
                  ? 'Enable multi-factor authentication'
                  : 'Disable multi-factor authentication'}
              </Typography>
            </Stack>

            <Divider />

            <Typography
              sx={{
                fontSize: '13px',
                color: 'text.secondary',
                lineHeight: 1.65,
                textAlign: 'center',
              }}
            >
              {mfaStatus
                ? "Adding MFA puts an extra lock on your account. You'll confirm your identity via Authenticator each time you sign in."
                : "Removing MFA means you'll only need your password to sign in — this reduces your account security."}
            </Typography>

            {/* Info / warning box */}
            <Box
              sx={{
                bgcolor: mfaStatus ? '#E6F1FB' : '#FAEEDA',
                border: `0.5px solid ${mfaStatus ? '#B5D4F4' : '#FAC775'}`,
                borderRadius: '8px',
                px: '14px',
                py: '10px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
              }}
            >
              <SvgIcon
                component={mfaStatus ? LockOutlined : WarningAmberOutlined}
                color={mfaStatus ? 'primary' : 'warning'}
                sx={{ fontSize: '14px', mt: '1px', flexShrink: 0 }}
              />
              <Typography
                sx={{
                  fontSize: '12px',
                  color: mfaStatus ? 'primary.main' : 'warning.main',
                  lineHeight: 1.6,
                }}
              >
                {mfaStatus
                  ? 'Make sure the Authenticator app is installed on your mobile device before proceeding.'
                  : 'Warning: disabling MFA may expose your account to unauthorized access. Proceed with caution.'}
              </Typography>
            </Box>

            <Divider />

            <Stack gap={1}>
              <Button
                variant='contained'
                fullWidth
                onClick={handleSetupMFA}
                disabled={loading}
                color={mfaStatus ? 'primary' : 'error'}
                sx={{ fontWeight: 500, fontSize: '13.5px', py: '9px' }}
              >
                {loading ? (
                  <CircularProgress size={20} color='inherit' />
                ) : mfaStatus ? (
                  'Enable MFA'
                ) : (
                  'Disable MFA'
                )}
              </Button>
              <Button
                variant='text'
                size='small'
                onClick={handleClose}
                fullWidth
                disabled={loading}
                sx={{ fontSize: '13px', color: 'text.secondary' }}
              >
                Cancel
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Stack alignItems='center' gap={1.25}>
              <IconCircle icon={PhonelinkOutlined} variant='verify' />
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '15px',
                  textAlign: 'center',
                  lineHeight: 1.4,
                }}
              >
                Scan &amp; verify
              </Typography>
            </Stack>

            <Divider />

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {mfaSecret?.qrCode ? (
                <Box
                  component='img'
                  src={mfaSecret.qrCode}
                  alt='MFA QR code'
                  sx={{
                    width: 140,
                    height: 140,
                    border: '0.5px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 140,
                    height: 140,
                    border: '0.5px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    bgcolor: 'background.default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                    QR code loading…
                  </Typography>
                </Box>
              )}
            </Box>

            <Typography
              sx={{
                fontSize: '12px',
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              Scan with Authenticator, then enter the 6-digit code below.
            </Typography>

            <TextField
              fullWidth
              label='Verification code'
              type='text'
              value={verificationCode}
              onChange={(e) =>
                setVerificationCode(
                  e.target.value.replace(/\D/g, '').slice(0, 6),
                )
              }
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyMFA()}
              placeholder='──────'
              inputProps={{
                maxLength: 6,
                style: {
                  textAlign: 'center',
                  fontSize: '22px',
                  letterSpacing: '10px',
                  fontWeight: 500,
                },
              }}
              InputLabelProps={{
                sx: { lineHeight: 2.1 },
              }}
              disabled={loading}
              size='small'
            />

            <Divider />

            <Stack gap={1}>
              <Button
                variant='contained'
                fullWidth
                onClick={handleVerifyMFA}
                disabled={loading || verificationCode.length !== 6}
                sx={{
                  fontWeight: 500,
                  fontSize: '13.5px',
                  py: '9px',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color='inherit' />
                ) : (
                  'Verify & enable'
                )}
              </Button>
              <Button
                variant='text'
                size='small'
                onClick={() => setStep('initial')}
                fullWidth
                disabled={loading}
                sx={{ fontSize: '13px', color: 'text.secondary' }}
              >
                Back
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Dialog>
  );
};

const DialogSetupMFA = (props: DialogSetupMFAProps) => {
  const isMFAEnabled = useAppSelector((state) => state.user.isMFAEnabled);
  return <SetupMFA key={`${props.open}-${isMFAEnabled}`} {...props} />;
};

export default DialogSetupMFA;
